import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, counterpartyApi, isinMasterApi } from '../services/api';
import { fetchSettlementModes } from '../services/paymentApi';
import DocumentList from './DocumentList';
import './DealSlipViewModal.css';

/** Buyback legs store both face and adjusted face; slips should show adjusted when set. */
function resolveBuybackLegFace(transaction, leg) {
  if (!transaction) return null;
  const prefix = leg === 1 ? 'leg1' : 'leg2';
  const adjusted = transaction[`${prefix}_adjusted_face_value`] ?? transaction[`${prefix}AdjustedFaceValue`];
  const face = transaction[`${prefix}_face_value`] ?? transaction[`${prefix}FaceValue`];
  if (adjusted !== null && adjusted !== undefined && adjusted !== '') return adjusted;
  return face;
}

/** Multi-lot sells store their true per-buy-deal split in sell_deal_allocations. */
function parseAllocationRows(raw) {
  if (!raw) return [];
  let allocs;
  try {
    allocs = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (_) {
    return [];
  }
  if (!Array.isArray(allocs)) return [];
  return allocs
    .map((a) => ({
      deal_number: a.deal_number || a.buy_deal_number,
      sold_face_value: a.amountToSell ?? a.amount ?? a.faceValue ?? null
    }))
    .filter((r) => r.deal_number);
}

function parseSellSourceAllocations(transaction, dealType) {
  if (!transaction) return [];

  if (dealType === 'Buyback') {
    const leg1Type = String(transaction.leg1_transaction_type || '').toLowerCase();
    if (leg1Type !== 'sell') return [];

    const allocRows = parseAllocationRows(transaction.sell_deal_allocations);
    if (allocRows.length) return allocRows;
    if (transaction.source_buy_deal_number) {
      return [{
        deal_number: transaction.source_buy_deal_number,
        sold_face_value:
          transaction.leg1_adjusted_face_value ??
          transaction.leg1AdjustedFaceValue ??
          transaction.leg1_face_value ??
          null
      }];
    }
    return [];
  }

  const txnType = String(transaction.transaction_type || transaction.transactionType || '').toLowerCase();
  if (txnType !== 'sell') return [];

  // Allocations are authoritative. buy_deal_number is only the primary lot, so
  // using it alone shows the entire sell against one source deal.
  const allocRows = parseAllocationRows(
    transaction.sell_deal_allocations ?? transaction.sellDealAllocations
  );
  if (allocRows.length) return allocRows;

  const buyDealNumber = transaction.buy_deal_number || transaction.buyDealNumber;
  if (buyDealNumber) {
    return [{
      deal_number: buyDealNumber,
      sold_face_value: transaction.face_value ?? transaction.faceValue ?? null
    }];
  }
  return [];
}

const DealSlipViewModal = ({ transaction, onClose, dealType = 'GSec', onApprove, onReject }) => {
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [counterpartyMap, setCounterpartyMap] = useState({});
  const [brokerMap, setBrokerMap] = useState({});
  const [settlementModeMap, setSettlementModeMap] = useState({});
  const [documentsExpanded, setDocumentsExpanded] = useState(false);
  const [isinCouponRate, setIsinCouponRate] = useState(null);
  const [sourceDealRows, setSourceDealRows] = useState([]);
  const [sourceDealsLoading, setSourceDealsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadCounterpartyMap = async () => {
      if (dealType !== 'Buyback') return;
      try {
        const rows = await counterpartyApi.getAll();
        if (!isMounted || !Array.isArray(rows)) return;
        const map = {};
        rows.forEach((cp) => {
          const display = cp.short_name || cp.long_name || '';
          if (!display) return;
          if (cp.unique_id) map[String(cp.unique_id).trim().toLowerCase()] = display;
          if (cp.id !== undefined && cp.id !== null && cp.id !== '') {
            map[String(cp.id).trim().toLowerCase()] = display;
          }
        });
        setCounterpartyMap(map);
      } catch (e) {
        // Keep graceful fallback to existing values when lookup fails.
      }
    };

    const loadBrokerMap = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/brokers`);
        if (!isMounted) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const map = {};
        list.forEach((b) => {
          const name = b.broker_name || b.name || '';
          if (!name) return;
          if (b.id !== undefined && b.id !== null) map[String(b.id).trim().toLowerCase()] = name;
          if (b.broker_code) map[String(b.broker_code).trim().toLowerCase()] = name;
        });
        setBrokerMap(map);
      } catch (e) {
        // graceful fallback
      }
    };

    const loadSettlementModes = async () => {
      try {
        const list = await fetchSettlementModes();
        if (!isMounted || !Array.isArray(list)) return;
        const map = {};
        list.forEach((m) => {
          if (m?.bank_payment_code) {
            map[String(m.bank_payment_code).trim().toLowerCase()] = m.payment_method || m.bank_payment_code;
          }
          if (m?.payment_method) {
            map[String(m.payment_method).trim().toLowerCase()] = m.payment_method;
          }
        });
        setSettlementModeMap(map);
      } catch (e) {
        // graceful fallback
      }
    };

    loadCounterpartyMap();
    loadBrokerMap();
    loadSettlementModes();
    return () => {
      isMounted = false;
    };
  }, [dealType]);

  // The GSec deal record stores coupon_interest (an amount) but not the coupon
  // rate %, which lives in the ISIN master. Resolve it so the deal slip can show it.
  // Note: the ISIN master "get by id" endpoint keys on the numeric PK, not the ISIN
  // string, so we load the full list and match on isin_number (same pattern as the
  // counterparty/broker/settlement-mode lookups above).
  useEffect(() => {
    let isMounted = true;
    const isin = transaction?.isin_number || transaction?.isin;
    const existing = transaction?.coupon_rate ?? transaction?.couponRate;
    if (existing !== undefined && existing !== null && existing !== '') {
      setIsinCouponRate(Number(existing));
      return undefined;
    }
    setIsinCouponRate(null);
    if (!isin || dealType === 'MoneyMarket') {
      return undefined;
    }
    (async () => {
      try {
        const rows = await isinMasterApi.getAll();
        if (!isMounted || !Array.isArray(rows)) return;
        const key = String(isin).trim().toLowerCase();
        const match = rows.find(
          (r) => String(r.isin_number || '').trim().toLowerCase() === key
        );
        const rate = match?.coupon_rate ?? match?.couponRate;
        if (rate !== undefined && rate !== null && rate !== '') {
          setIsinCouponRate(Number(rate));
        }
      } catch (e) {
        // graceful fallback - leave coupon rate unset
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [dealType, transaction?.isin_number, transaction?.isin, transaction?.coupon_rate, transaction?.couponRate]);

  // Resolve source buy-deal details for sell / sell-buyback slips (from sell modal allocations).
  useEffect(() => {
    let isMounted = true;
    const allocRows = parseSellSourceAllocations(transaction, dealType);
    if (!allocRows.length) {
      setSourceDealRows([]);
      setSourceDealsLoading(false);
      return undefined;
    }

    const dealNumbers = [...new Set(allocRows.map((r) => r.deal_number).filter(Boolean))];
    setSourceDealsLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/isin-master/gsec/source-buy-deals`, {
          params: { deal_numbers: dealNumbers.join(',') }
        });
        if (!isMounted) return;
        const buyMap = {};
        (res.data?.data || []).forEach((d) => {
          if (d.deal_number) buyMap[d.deal_number] = d;
        });
        setSourceDealRows(
          allocRows.map((alloc) => {
            const buy = buyMap[alloc.deal_number] || {};
            return {
              deal_number: alloc.deal_number,
              yield: buy.yield,
              face_value: buy.face_value,
              sold_face_value: alloc.sold_face_value
            };
          })
        );
      } catch (_) {
        if (isMounted) {
          setSourceDealRows(
            allocRows.map((alloc) => ({
              deal_number: alloc.deal_number,
              yield: null,
              face_value: null,
              sold_face_value: alloc.sold_face_value
            }))
          );
        }
      } finally {
        if (isMounted) setSourceDealsLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [dealType, transaction]);

  const resolveBroker = (raw) => {
    if (raw === null || raw === undefined || raw === '') return 'N/A';
    const trimmed = String(raw).trim();
    // Treat zero / placeholder as "no broker"
    if (!trimmed || trimmed === '0' || trimmed.toLowerCase() === 'null') return 'No Broker';
    const key = trimmed.toLowerCase();
    return brokerMap[key] || trimmed;
  };

  const isResolvedBroker = (raw) => {
    if (raw === null || raw === undefined || raw === '') return false;
    const trimmed = String(raw).trim();
    if (!trimmed || trimmed === '0' || trimmed.toLowerCase() === 'null') return false;
    return Boolean(brokerMap[trimmed.toLowerCase()]);
  };

  const resolveSettlementMode = (raw) => {
    if (raw === null || raw === undefined || raw === '') return 'N/A';
    const key = String(raw).trim().toLowerCase();
    return settlementModeMap[key] || String(raw);
  };

  if (!transaction) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPrice = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toFixed(4);
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toFixed(4);
  };

  const formatRepoDealType = (dt) => {
    if (!dt) return 'Repo';
    const normalized = String(dt).toLowerCase().replace(/[\s_-]+/g, '');
    if (normalized === 'reverserepo') return 'Reverse Repo';
    if (normalized === 'repo') return 'Repo';
    return dt;
  };

  // Get counterparty details (this would need to be fetched or passed in)
  const counterpartyName = transaction.counterparty_name || transaction.counterparty || 'N/A';
  const resolveCounterpartyDisplay = (rawValue, explicitName, fallbackName = '') => {
    if (explicitName) return explicitName;
    const raw = rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
    if (!raw) return fallbackName || 'N/A';
    const mapped = counterpartyMap[raw.toLowerCase()];
    return mapped || fallbackName || raw;
  };
  const leg1CounterpartyDisplay = resolveCounterpartyDisplay(
    transaction.leg1_counterparty,
    transaction.leg1_counterparty_name || transaction.leg1_counterparty_short_name || '',
    counterpartyName
  );
  const leg2CounterpartyDisplay = resolveCounterpartyDisplay(
    transaction.leg2_counterparty || transaction.leg1_counterparty,
    transaction.leg2_counterparty_name || transaction.leg2_counterparty_short_name || '',
    leg1CounterpartyDisplay
  );

  const handleApprove = async () => {
    setIsApproving(true);
    setError('');
    setSuccess('');
    try {
      // If callbacks are provided, use them; otherwise make API call directly
      if (onApprove) {
        await onApprove(transaction.id);
        setSuccess('Transaction approved successfully');
      } else {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        await axios.put(`${API_BASE_URL}/isin-master/gsec/${transaction.id}/status`, {
          status: 'approved',
          userId: user.id
        });
        setSuccess('Transaction approved successfully');
      }
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setError('Failed to approve transaction: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      setError('Comment is required when rejecting a transaction');
      return;
    }
    setIsRejecting(true);
    setError('');
    setSuccess('');
    try {
      // If callbacks are provided, use them; otherwise make API call directly
      if (onReject) {
        await onReject(transaction.id, rejectComment);
        setSuccess('Transaction rejected successfully');
      } else {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        await axios.put(`${API_BASE_URL}/isin-master/gsec/${transaction.id}/status`, {
          status: 'rejected',
          comment: rejectComment,
          userId: user.id
        });
        setSuccess('Transaction rejected successfully');
      }
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setError('Failed to reject transaction: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsRejecting(false);
      setShowRejectDialog(false);
      setRejectComment('');
    }
  };

  const openRejectDialog = () => {
    setShowRejectDialog(true);
    setError('');
  };

  const closeRejectDialog = () => {
    setShowRejectDialog(false);
    setRejectComment('');
    setError('');
  };

  // Only show approve/reject buttons if transaction is pending
  const showActionButtons = transaction.status === 'pending' || 
                           transaction.current_approval_level === 'back_office_verifier' ||
                           transaction.current_approval_level === 2;

  const renderSourceDealsSection = () => {
    const showForGsecSell =
      dealType !== 'Buyback' &&
      dealType !== 'Repo' &&
      dealType !== 'MoneyMarket' &&
      String(transaction.transaction_type || transaction.transactionType || '').toLowerCase() === 'sell';
    const showForBuybackSell =
      dealType === 'Buyback' &&
      String(transaction.leg1_transaction_type || '').toLowerCase() === 'sell';
    if (!showForGsecSell && !showForBuybackSell) return null;
    if (!sourceDealsLoading && !sourceDealRows.length) return null;

    return (
      <div className="deal-slip-section full-span" data-tone="instrument">
        <h3>Source Deal(s)</h3>
        {sourceDealsLoading ? (
          <div className="field-value" style={{ fontStyle: 'italic', color: '#94a3b8' }}>
            Loading source deal details...
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255,255,255,0.6)', borderRadius: 6, overflow: 'hidden' }}>
            <thead style={{ background: '#eff6ff' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Source Deal Number</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Yield %</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Face Value</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount Sold</th>
              </tr>
            </thead>
            <tbody>
              {sourceDealRows.map((row, idx) => (
                <tr key={`${row.deal_number}-${idx}`}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #dbeafe', fontWeight: 500 }}>{row.deal_number}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #dbeafe', textAlign: 'right' }}>
                    {row.yield != null && row.yield !== '' ? formatPercentage(row.yield) : 'N/A'}
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #dbeafe', textAlign: 'right', fontWeight: 600 }}>
                    {row.face_value != null && row.face_value !== '' ? formatCurrency(row.face_value) : 'N/A'}
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #dbeafe', textAlign: 'right' }}>
                    {row.sold_face_value != null && row.sold_face_value !== '' ? formatCurrency(row.sold_face_value) : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
            {sourceDealRows.length > 1 && (
              <tfoot>
                <tr style={{ background: '#eff6ff' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 700 }} colSpan={3}>Total</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(
                      sourceDealRows.reduce((sum, r) => sum + (Number(r.sold_face_value) || 0), 0)
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="deal-slip-modal-overlay" onClick={onClose}>
      <div className="deal-slip-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="deal-slip-header">
          <h2>{(() => {
            if (dealType === 'Buyback') return 'Deal Slip — Buyback Agreement';
            if (dealType === 'MoneyMarket') {
              const t = transaction.product_type || transaction.deal_type || '';
              return `Deal Slip — Money Market${t ? ` (${t})` : ''}`;
            }
            if (dealType === 'Repo') {
              const t = transaction.deal_type || transaction.product_type || 'Repo';
              return `Deal Slip — ${t}`;
            }
            if (transaction.transaction_type === 'Buy') return 'Deal Slip — Buy Agreement';
            if (transaction.transaction_type === 'Sell') return 'Deal Slip — Sell Agreement';
            return 'Deal Slip';
          })()}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="deal-slip-body">
          {/* Hero summary strip - shows the deal at a glance */}
          {(() => {
            const dealNo = transaction.deal_number || transaction.dealNumber || transaction.deal_no || transaction.id || 'N/A';
            const txnType = transaction.transaction_type || transaction.transactionType || transaction.deal_type || transaction.product_type || '';
            const cpDisplay = dealType === 'Buyback'
              ? leg1CounterpartyDisplay
              : (transaction.counterparty_name || transaction.counterparty || '');
            const settlement =
              transaction.settlement_amount ||
              transaction.settlementAmount ||
              transaction.principal_amount ||
              transaction.maturity_value;
            const statusText = transaction.status || transaction.approval_status || transaction.deal_status || 'pending';
            const statusKey = String(statusText).toLowerCase().replace(/\s+/g, '_');
            const isBuy = /buy/i.test(String(txnType));
            const isSell = /sell/i.test(String(txnType));
            return (
              <div className="deal-slip-hero">
                <div className="hero-primary">
                  <span className="deal-no">{dealNo}</span>
                  <span className="deal-sub">
                    {cpDisplay ? `Counterparty: ${cpDisplay}` : 'No counterparty'}
                    {settlement ? ` · ${formatCurrency(settlement)} LKR` : ''}
                  </span>
                </div>
                <div className="hero-badges">
                  {dealType !== 'Buyback' && txnType && (isBuy || isSell) && (
                    <span className={`txn-pill ${isBuy ? 'buy' : 'sell'}`}>
                      {isBuy ? '↘ Buy' : '↗ Sell'}
                    </span>
                  )}
                  {txnType && !isBuy && !isSell && (
                    <span className="txn-pill buy" style={{ background: '#e0e7ff', color: '#3730a3', borderColor: '#a5b4fc' }}>
                      {String(txnType).toUpperCase()}
                    </span>
                  )}
                  <span className={`status-badge ${statusKey}`}>
                    {String(statusText).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </div>
              </div>
            );
          })()}

          <div className="deal-slip-grid-wrapper">

          {dealType === 'Buyback' && (
            <>
              <div className="deal-slip-section full-span" data-tone="deal">
                <h3>Buyback Deal Details</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Deal Number</label>
                    <div className="field-value">{transaction.deal_number || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Status</label>
                    <div className="field-value">{transaction.deal_status || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Created By</label>
                    <div className="field-value">{transaction.created_by_name || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Created At</label>
                    <div className="field-value">{formatDate(transaction.created_at)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Verified By</label>
                    <div className="field-value">{transaction.verified_by_name || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Approved By</label>
                    <div className="field-value">{transaction.approved_by_name || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div className="deal-slip-section" data-tone="pricing">
                <h3>Leg 1 Details</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Trade Date</label>
                    <div className="field-value">{formatDate(transaction.leg1_trade_date)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Value Date</label>
                    <div className="field-value">{formatDate(transaction.leg1_value_date)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Transaction Type</label>
                    <div className="field-value">{transaction.leg1_transaction_type || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Trade Type</label>
                    <div className="field-value">{transaction.leg1_trade_type || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>ISIN</label>
                    <div className="field-value">{transaction.leg1_isin || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Counterparty</label>
                    <div className="field-value">{leg1CounterpartyDisplay}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Face Value</label>
                    <div className="field-value">{formatCurrency(resolveBuybackLegFace(transaction, 1))}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Settlement Amount</label>
                    <div className="field-value">{formatCurrency(transaction.leg1_settlement_amount)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Yield %</label>
                    <div className="field-value">{formatPercentage(transaction.leg1_yield_rate)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Clean Price</label>
                    <div className="field-value">{formatPrice(transaction.leg1_clean_price)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Dirty Price</label>
                    <div className="field-value">{formatPrice(transaction.leg1_dirty_price)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Accrued Interest</label>
                    <div className="field-value">{formatPrice(transaction.leg1_accrued_interest)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Interest Rate %</label>
                    <div className="field-value">{formatPercentage(transaction.leg1_interest_rate)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Settlement Mode</label>
                    <div className="field-value">{resolveSettlementMode(transaction.leg1_settlement_mode)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Portfolio</label>
                    <div className="field-value">{transaction.leg1_portfolio || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Strategy</label>
                    <div className="field-value">{transaction.leg1_strategy || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Custodian</label>
                    <div className="field-value">{transaction.leg1_custodian || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Currency</label>
                    <div className="field-value">{transaction.leg1_currency || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {renderSourceDealsSection()}

              <div className="deal-slip-section" data-tone="pricing">
                <h3>Leg 2 Details</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Trade Date</label>
                    <div className="field-value">{formatDate(transaction.leg2_trade_date)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Value Date</label>
                    <div className="field-value">{formatDate(transaction.leg2_value_date)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Transaction Type</label>
                    <div className="field-value">{transaction.leg2_transaction_type || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Trade Type</label>
                    <div className="field-value">{transaction.leg2_trade_type || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>ISIN</label>
                    <div className="field-value">{transaction.leg2_isin || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Counterparty</label>
                    <div className="field-value">{leg2CounterpartyDisplay}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Face Value</label>
                    <div className="field-value">{formatCurrency(resolveBuybackLegFace(transaction, 2))}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Settlement Amount</label>
                    <div className="field-value">{formatCurrency(transaction.leg2_settlement_amount)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Yield %</label>
                    <div className="field-value">{formatPercentage(transaction.leg2_yield_rate)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Clean Price</label>
                    <div className="field-value">{formatPrice(transaction.leg2_clean_price)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Dirty Price</label>
                    <div className="field-value">{formatPrice(transaction.leg2_dirty_price)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Accrued Interest</label>
                    <div className="field-value">{formatPrice(transaction.leg2_accrued_interest)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Settlement Mode</label>
                    <div className="field-value">{resolveSettlementMode(transaction.leg2_settlement_mode)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Portfolio</label>
                    <div className="field-value">{transaction.leg2_portfolio || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Strategy</label>
                    <div className="field-value">{transaction.leg2_strategy || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Custodian</label>
                    <div className="field-value">{transaction.leg2_custodian || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Currency</label>
                    <div className="field-value">{transaction.leg2_currency || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div className="deal-slip-section full-span" data-tone="instrument">
                <h3>Security Metadata</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Issue Date</label>
                    <div className="field-value">{formatDate(transaction.issue_date)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Maturity Date</label>
                    <div className="field-value">{formatDate(transaction.maturity_date)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Coupon Rate %</label>
                    <div className="field-value">{formatPercentage(transaction.coupon_rate)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Coupon Date 1</label>
                    <div className="field-value">{transaction.coupon_date1 || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Coupon Date 2</label>
                    <div className="field-value">{transaction.coupon_date2 || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field full-width">
                    <label>Notes</label>
                    <div className="field-value">{transaction.notes || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {dealType !== 'Buyback' && dealType !== 'Repo' && dealType !== 'MoneyMarket' && (
            <>
          {/* Block 1: Deal Details */}
          <div className="deal-slip-section full-span" data-tone="deal">
            <h3>Deal Details</h3>
            <div className="deal-slip-grid">
              <div className="deal-slip-field">
                <label>Deal No</label>
                <div className="field-value">{transaction.deal_number || transaction.dealNumber || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Deal Type</label>
                <div className="field-value">
                  {transaction.transaction_type === 'Buy' ? 'Buy' : transaction.transaction_type === 'Sell' ? 'Sell' : transaction.transactionType || 'N/A'}
                </div>
              </div>
              <div className="deal-slip-field">
                <label>Counterparty</label>
                <div className="field-value">{counterpartyName}</div>
              </div>
              <div className="deal-slip-field">
                <label>Trade Date</label>
                <div className="field-value">{formatDate(transaction.trade_date || transaction.tradeDate) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Value Date</label>
                <div className="field-value">{formatDate(transaction.value_date || transaction.valueDate) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Maturity Date</label>
                <div className="field-value">{formatDate(transaction.maturity_date || transaction.maturityDate) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Face Value</label>
                <div className="field-value">{formatCurrency(transaction.face_value || transaction.faceValue) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Accrued Interest</label>
                <div className="field-value">{formatCurrency(transaction.accrued_interest || transaction.accruedInterest) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Settlement Value</label>
                <div className="field-value">{formatCurrency(transaction.settlement_amount || transaction.settlementAmount) || 'N/A'}</div>
              </div>
            </div>
          </div>

          {renderSourceDealsSection()}

          {/* Block 2: Instrument & Pricing */}
          <div className="deal-slip-section" data-tone="pricing">
            <h3>Instrument &amp; Pricing</h3>
            <div className="deal-slip-grid">
              <div className="deal-slip-field">
                <label>ISIN</label>
                <div className="field-value">{transaction.isin || transaction.isin_number || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Issue Date</label>
                <div className="field-value">{formatDate(transaction.issue_date || transaction.issueDate) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Coupon Rate %</label>
                <div className="field-value">{isinCouponRate !== null ? formatPercentage(isinCouponRate) : 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Yield %</label>
                <div className="field-value">{formatPercentage(transaction.yield || transaction.yieldRate || transaction.discount_rate_pct) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Clean Price</label>
                <div className="field-value">{formatPrice(transaction.clean_price || transaction.cleanPrice) || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Dirty Price</label>
                <div className="field-value">{formatPrice(transaction.dirty_price || transaction.dirtyPrice) || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Block 3: Portfolio & Settlement */}
          <div className="deal-slip-section" data-tone="settlement">
            <h3>Portfolio &amp; Settlement</h3>
            <div className="deal-slip-grid">
              <div className="deal-slip-field">
                <label>Portfolio</label>
                <div className="field-value">{transaction.portfolio || transaction.portfolio_id || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Strategy</label>
                <div className="field-value">{transaction.strategy || transaction.strategy_id || 'N/A'}</div>
              </div>
              <div className="deal-slip-field">
                <label>Broker</label>
                <div className="field-value">
                  {resolveBroker(transaction.broker || transaction.broker_id)}
                  {isResolvedBroker(transaction.broker || transaction.broker_id) && (
                    <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>(#{transaction.broker || transaction.broker_id})</span>
                  )}
                </div>
              </div>
              <div className="deal-slip-field">
                <label>Brokerage</label>
                <div className="field-value">
                  {(transaction.brokerage !== undefined && transaction.brokerage !== null && transaction.brokerage !== '')
                    ? formatCurrency(transaction.brokerage)
                    : 'N/A'}
                </div>
              </div>
              <div className="deal-slip-field">
                <label>Settlement Mode</label>
                <div className="field-value">
                  {resolveSettlementMode(transaction.settlement_mode || transaction.settlementMode)}
                  {(transaction.settlement_mode || transaction.settlementMode) &&
                    settlementModeMap[String(transaction.settlement_mode || transaction.settlementMode).trim().toLowerCase()]
                    ? <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>(code {transaction.settlement_mode || transaction.settlementMode})</span>
                    : null}
                </div>
              </div>
              <div className="deal-slip-field">
                <label>Custodian</label>
                <div className="field-value">{transaction.custodian || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Comments/Remarks */}
          {transaction.comment && (
            <div className="deal-slip-section full-span" data-tone="comment">
              <h3>Comments</h3>
              <div className="deal-slip-remarks">
                {transaction.comment}
              </div>
            </div>
          )}
            </>
          )}

          {/* Money Market deal slip */}
          {dealType === 'MoneyMarket' && (
            <>
              {/* Block 1: Deal Details */}
              <div className="deal-slip-section full-span" data-tone="deal">
                <h3>Deal Details</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Deal No</label>
                    <div className="field-value">{transaction.deal_number || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Product Type</label>
                    <div className="field-value">{transaction.product_type || transaction.deal_type || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Counterparty</label>
                    <div className="field-value">{transaction.counterparty_name || transaction.counterparty_id || transaction.counterparty || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Trade Date</label>
                    <div className="field-value">{formatDate(transaction.trade_date) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Value Date</label>
                    <div className="field-value">{formatDate(transaction.value_date) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Maturity Date</label>
                    <div className="field-value">{formatDate(transaction.maturity_date) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Tenor (days)</label>
                    <div className="field-value">{transaction.tenor || transaction.tenure || transaction.days_to_maturity || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Currency</label>
                    <div className="field-value">{transaction.currency || 'LKR'}</div>
                  </div>
                </div>
              </div>

              {/* Block 2: Amounts & Interest */}
              <div className="deal-slip-section" data-tone="pricing">
                <h3>Amounts &amp; Interest</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Principal</label>
                    <div className="field-value">{formatCurrency(transaction.principal_amount) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Interest Rate %</label>
                    <div className="field-value">{formatPercentage(transaction.interest_rate) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Interest Amount</label>
                    <div className="field-value">{formatCurrency(transaction.interest_amount) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Maturity Value</label>
                    <div className="field-value">{formatCurrency(transaction.maturity_value) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Day Basis</label>
                    <div className="field-value">{transaction.day_basis || transaction.day_count || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Block 3: Portfolio & Settlement */}
              <div className="deal-slip-section" data-tone="settlement">
                <h3>Portfolio &amp; Settlement</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Portfolio</label>
                    <div className="field-value">{transaction.portfolio || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Strategy</label>
                    <div className="field-value">{transaction.strategy || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Broker</label>
                    <div className="field-value">
                      {resolveBroker(transaction.broker)}
                      {isResolvedBroker(transaction.broker) && (
                        <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>(#{transaction.broker})</span>
                      )}
                    </div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Settlement Mode</label>
                    <div className="field-value">
                      {resolveSettlementMode(transaction.settlement_mode || transaction.settlementMode)}
                    </div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Custodian</label>
                    <div className="field-value">{transaction.custodian || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {transaction.comment && (
                <div className="deal-slip-section full-span" data-tone="comment">
                  <h3>Comments</h3>
                  <div className="deal-slip-remarks">{transaction.comment}</div>
                </div>
              )}
            </>
          )}

          {/* Repo / Reverse Repo deal slip */}
          {dealType === 'Repo' && (() => {
            const allocations =
              Array.isArray(transaction.isins) && transaction.isins.length > 0
                ? transaction.isins
                : (transaction.collateral_isin || transaction.isin)
                ? [{ isin: transaction.collateral_isin || transaction.isin, faceValue: transaction.collateral_face_value || transaction.face_value }]
                : [];
            const totalAllocatedFace = allocations.reduce((sum, a) => sum + (Number(a.faceValue) || 0), 0);

            return (
            <>
              {/* Block 1: Deal Details */}
              <div className="deal-slip-section full-span" data-tone="deal">
                <h3>Deal Details</h3>
                <div className="deal-slip-grid">
                  <div className="deal-slip-field">
                    <label>Deal Type</label>
                    <div className="field-value">{formatRepoDealType(transaction.deal_type || transaction.product_type)}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Trade Date</label>
                    <div className="field-value">{formatDate(transaction.trade_date) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Value Date</label>
                    <div className="field-value">{formatDate(transaction.value_date) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Counterparty</label>
                    <div className="field-value">{transaction.counterparty_name || transaction.counterparty_id || transaction.counterparty || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Maturity Date</label>
                    <div className="field-value">{formatDate(transaction.maturity_date) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Principal Amount</label>
                    <div className="field-value">{formatCurrency(transaction.principal_amount) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Rate %</label>
                    <div className="field-value">{formatPercentage(transaction.rate || transaction.repo_rate) || 'N/A'}</div>
                  </div>
                  <div className="deal-slip-field">
                    <label>Maturity Amount</label>
                    <div className="field-value">{formatCurrency(transaction.maturity_amount || transaction.maturity_value) || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Block 2: Collateral Allocation (ISIN-wise Face Value) */}
              <div className="deal-slip-section full-span" data-tone="instrument">
                <h3>Collateral Allocation (ISIN-wise Face Value)</h3>
                {allocations.length === 0 ? (
                  <div className="field-value" style={{ fontStyle: 'italic', color: '#94a3b8' }}>No ISIN allocations recorded.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255,255,255,0.6)', borderRadius: 6, overflow: 'hidden' }}>
                    <thead style={{ background: '#f3e8ff' }}>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e9d5ff', fontSize: 12, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e9d5ff', fontSize: 12, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ISIN</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid #e9d5ff', fontSize: 12, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Face Value Allocated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocations.map((item, idx) => (
                        <tr key={`${item.isin || 'isin'}-${idx}`}>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3e8ff' }}>{idx + 1}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3e8ff', fontWeight: 500 }}>{item.isin || ''}</td>
                          <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3e8ff', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.faceValue)}</td>
                        </tr>
                      ))}
                      {allocations.length > 1 && (
                        <tr style={{ background: '#faf5ff', fontWeight: 700 }}>
                          <td style={{ padding: '8px 12px' }} colSpan={2}>Total</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(totalAllocatedFace)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {transaction.comment && (
                <div className="deal-slip-section full-span" data-tone="comment">
                  <h3>Comments</h3>
                  <div className="deal-slip-remarks">{transaction.comment}</div>
                </div>
              )}
            </>
            );
          })()}

          {/* Audit Timeline - who touched this deal and when */}
          {(() => {
            const fmtDateTime = (v) => {
              if (!v) return '';
              try {
                const d = new Date(v);
                if (Number.isNaN(d.getTime())) return '';
                return d.toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', hour12: false,
                });
              } catch {
                return '';
              }
            };

            const createdBy = transaction.created_by_name || transaction.username || transaction.created_by || 'Front Office';
            const createdAt = transaction.created_at || transaction.createdAt;
            const updatedBy = transaction.updated_by_name || transaction.authorized_by || transaction.updated_by;
            const updatedAt = transaction.updated_at || transaction.updatedAt;
            // Buybacks keep their stage in deal_status alone (no current_approval_level),
            // so derive the level from it - otherwise an approved buyback reads as stuck
            // after the front-office step.
            const BUYBACK_STAGE_LEVEL = {
              draft: 'front_office',
              pending_verification: 'front_office',
              verified: 'back_office_verifier',
              pending_final_approval: 'back_office_final',
              approved: 'final_approved',
              settled: 'final_approved',
            };
            const status = String(transaction.status || transaction.deal_status || '').toLowerCase();
            const level = String(
              transaction.current_approval_level ||
              (transaction.deal_status ? BUYBACK_STAGE_LEVEL[status] : '') ||
              ''
            ).toLowerCase();
            const isRejected = status === 'rejected';
            const isFinal = ['final_approved', 'approved', 'settled'].includes(status) || level === 'final_approved';
            const foBy = transaction.front_office_by_name;
            const boVerifierBy = transaction.back_office_verifier_by_name || transaction.verified_by_name;
            const finalBy = transaction.final_approved_by_name || transaction.approved_by_name;
            const finalAt = transaction.approved_at || updatedAt;
            const byLine = (text, who) => (who ? `${text} by ${who}` : text);

            // Step state helper
            // 0 = pending/upcoming, 1 = current, 2 = done, 3 = rejected
            const stepStates = {
              created: 2,
              foCheck: level === 'front_office' && !isRejected ? 1 : (isRejected || level !== 'front_office' ? 2 : 0),
              boVerifier:
                level === 'back_office_verifier' ? 1 :
                isRejected && level === 'front_office' ? 3 :
                (level === 'back_office_final' || isFinal) ? 2 : 0,
              boFinal:
                level === 'back_office_final' ? 1 :
                isFinal ? 2 : 0,
              finalApproved: isFinal ? 2 : 0,
            };

            // If rejected, only the rejection step is "current/red"
            const rejectionStep = isRejected ? {
              title: 'Rejected — sent back to Front Office',
              meta: `${updatedBy ? `By ${updatedBy}` : 'Reviewer'}${updatedAt ? ` · ${fmtDateTime(updatedAt)}` : ''}`,
              dotClass: 'rejected',
              dotLabel: '✕',
              comment: transaction.comment || null,
            } : null;

            const dotClass = (s) => s === 2 ? 'done' : s === 1 ? 'current' : s === 3 ? 'rejected' : '';
            const dotLabel = (s) => s === 2 ? '✓' : s === 1 ? '●' : s === 3 ? '✕' : '○';

            return (
              <div className="deal-slip-section full-span" data-tone="audit">
                <h3>Audit Timeline</h3>
                <ul className="audit-timeline">
                  <li className="audit-step">
                    <span className={`audit-dot ${dotClass(stepStates.created)}`}>{dotLabel(stepStates.created)}</span>
                    <div className="audit-title">Created by {createdBy}</div>
                    <div className="audit-meta">{createdAt ? fmtDateTime(createdAt) : 'Initial entry'}</div>
                  </li>

                  <li className="audit-step">
                    <span className={`audit-dot ${dotClass(stepStates.foCheck)}`}>{dotLabel(stepStates.foCheck)}</span>
                    <div className="audit-title">Front-Office Checker</div>
                    <div className="audit-meta">
                      {stepStates.foCheck === 1 ? 'Awaiting front-office review' :
                       stepStates.foCheck === 2 ? byLine('Submitted for verification', foBy) : 'Pending'}
                    </div>
                  </li>

                  <li className="audit-step">
                    <span className={`audit-dot ${dotClass(stepStates.boVerifier)}`}>{dotLabel(stepStates.boVerifier)}</span>
                    <div className="audit-title">Back-Office Verifier</div>
                    <div className="audit-meta">
                      {stepStates.boVerifier === 1 ? 'Awaiting first authorisation' :
                       stepStates.boVerifier === 2 ? byLine('Verified', boVerifierBy) :
                       stepStates.boVerifier === 3 ? 'Rejected here · sent back to front office' :
                       'Pending'}
                    </div>
                  </li>

                  <li className="audit-step">
                    <span className={`audit-dot ${dotClass(stepStates.boFinal)}`}>{dotLabel(stepStates.boFinal)}</span>
                    <div className="audit-title">Back-Office Final Authorisation</div>
                    <div className="audit-meta">
                      {stepStates.boFinal === 1 ? 'Awaiting final approval' :
                       stepStates.boFinal === 2 ? byLine('Approved', finalBy) : 'Pending'}
                    </div>
                  </li>

                  <li className="audit-step">
                    <span className={`audit-dot ${dotClass(stepStates.finalApproved)}`}>{dotLabel(stepStates.finalApproved)}</span>
                    <div className="audit-title">Final Approved</div>
                    <div className="audit-meta">
                      {stepStates.finalApproved === 2
                        ? `Posted to ledger${finalAt ? ` · ${fmtDateTime(finalAt)}` : ''}`
                        : 'Pending'}
                    </div>
                  </li>

                  {rejectionStep && (
                    <li className="audit-step">
                      <span className={`audit-dot ${rejectionStep.dotClass}`}>{rejectionStep.dotLabel}</span>
                      <div className="audit-title">{rejectionStep.title}</div>
                      <div className="audit-meta">{rejectionStep.meta}</div>
                      {rejectionStep.comment && (
                        <div className="audit-comment">"{rejectionStep.comment}"</div>
                      )}
                    </li>
                  )}
                </ul>
              </div>
            );
          })()}

          {/* Documents Section - collapsible, full-width */}
          <div className="deal-slip-section full-span" data-tone="documents">
            <button
              type="button"
              onClick={() => setDocumentsExpanded((v) => !v)}
              aria-expanded={documentsExpanded}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '8px 12px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 700,
                color: '#4f46e5',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-paperclip" aria-hidden="true" />
                Documents
              </span>
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  transition: 'transform 0.2s ease',
                  transform: documentsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  fontSize: 14,
                  color: '#6b7280',
                }}
              >
                ▶
              </span>
            </button>
            {documentsExpanded && (
              <div style={{ marginTop: 10, padding: '8px 4px' }}>
                <DocumentList
                  transactionType={
                    dealType === 'GSec' ? 'gsec'
                    : dealType === 'MoneyMarket' || dealType === 'Money Market' ? 'money_market'
                    : dealType === 'Buyback' ? 'buyback'
                    : dealType === 'Repo' ? 'repo'
                    : 'transaction'
                  }
                  transactionId={transaction.id || transaction.deal_number || transaction.dealNumber || transaction._id}
                  onDocumentDeleted={() => {}}
                />
              </div>
            )}
          </div>

          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="deal-slip-message deal-slip-error">
              {error}
            </div>
          )}
          {success && (
            <div className="deal-slip-message deal-slip-success">
              {success}
            </div>
          )}
        </div>

        <div className="deal-slip-footer">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Approve / Reject actions removed - this modal is now view-only. */}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="deal-slip-button" onClick={onClose}>Close</button>
            <button className="deal-slip-button primary" onClick={() => window.print()}>Print</button>
          </div>
        </div>

        {/* Reject Dialog */}
        {showRejectDialog && (
          <div className="deal-slip-reject-dialog-overlay" onClick={closeRejectDialog}>
            <div className="deal-slip-reject-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Reject Transaction</h3>
              <p>Please provide a reason for rejecting this transaction:</p>
              <textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Enter rejection reason..."
                className="deal-slip-reject-textarea"
                rows="4"
              />
              {error && (
                <div className="deal-slip-message deal-slip-error" style={{ marginTop: '8px' }}>
                  {error}
                </div>
              )}
              <div className="deal-slip-reject-dialog-actions">
                <button 
                  className="deal-slip-button" 
                  onClick={closeRejectDialog}
                  disabled={isRejecting}
                >
                  Cancel
                </button>
                <button 
                  className="deal-slip-button reject" 
                  onClick={handleReject}
                  disabled={isRejecting || !rejectComment.trim()}
                >
                  {isRejecting ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default DealSlipViewModal;

